import { computeSlaCountdown, isSlaClockFrozen } from './slaCountdown';

/**
 * Rows out of a master/list response, whatever wrapper it uses.
 * Inquiry master services already unwrap to a plain array; `wrapResponse` clients
 * return `{ data }` (roles) or `{ data: { data } }` (users with role); pages return `{ content }`.
 */
export function masterRows(response) {
  const root = response?.data ?? response;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.content)) return root.content;
  if (Array.isArray(root?.data)) return root.data;
  return [];
}

/** Option value for a <Select>; ids arrive as numbers but option values must be strings. */
export function optionValue(id) {
  return id === null || id === undefined ? '' : String(id);
}

/**
 * Optional id filter: 'all' means "no filter", so return undefined and let the key be omitted
 * rather than sending 0, "", or "All" to a Long parameter.
 */
export function optionalId(value) {
  if (value === null || value === undefined || value === 'all' || value === '') return undefined;
  const n = Number(value);
  return Number.isNaN(n) ? undefined : n;
}

/** Optional tri-state filter: 'yes' -> true, 'no' -> false, 'all' -> undefined (omitted). */
export function optionalBool(value) {
  if (value === 'yes' || value === true) return true;
  if (value === 'no' || value === false) return false;
  return undefined;
}

/** Drops keys whose value is undefined so "All" never reaches the request body. */
export function compactFilters(filters) {
  Object.keys(filters).forEach((key) => filters[key] === undefined && delete filters[key]);
  return filters;
}

/** Prefer Inquiry Category; never invent "General" when Inquiry name exists. */
export function resolveTicketCategoryLabel(ticket) {
  if (!ticket) return '—';
  return (
    ticket.inquiryCategoryName
    || ticket.categoryName
    || ticket.category
    || '—'
  );
}

/** Prefer Inquiry Priority Level over legacy Low/Medium/High. */
export function resolveTicketPriorityLabel(ticket) {
  if (!ticket) return '—';
  return (
    ticket.inquiryPriorityLevelName
    || ticket.priorityName
    || (typeof ticket.priority === 'string' ? ticket.priority : null)
    || '—'
  );
}

/** Type › Category › Sub Category for Inquiry rows; empty string for legacy. */
export function resolveInquiryBreadcrumb(ticket) {
  if (!ticket) return '';
  return [ticket.inquiryTypeName, ticket.inquiryCategoryName, ticket.inquirySubCategoryName]
    .filter(Boolean)
    .join(' › ');
}

/**
 * Label for the CURRENT SLA window (not historical slaIsBreached alone).
 * @returns {{ label: string, isOverdue: boolean, minutes: number, seconds: number } | null}
 */
export function resolveCurrentSlaWindow(ticket, tickSeconds = 0, statuses) {
  if (!ticket) return null;
  if (
    ticket.slaRemainingMinutes === null
    || ticket.slaRemainingMinutes === undefined
  ) {
    if (ticket.slaApplicable === false) return { label: 'No SLA', isOverdue: false, minutes: 0, seconds: 0 };
    return null;
  }

  const frozen = isSlaClockFrozen(ticket, statuses) || ticket.slaUsesBusinessHours === true;
  const countdown = computeSlaCountdown(
    {
      remainingMinutes: ticket.slaRemainingMinutes,
      remainingSeconds: ticket.slaRemainingSeconds,
      overdueMinutes: ticket.slaOverdueMinutes,
      overdueSeconds: ticket.slaOverdueSeconds,
    },
    frozen ? 0 : tickSeconds,
    { slaUsesBusinessHours: ticket.slaUsesBusinessHours === true }
  );

  if (countdown.overdueMinutes !== null) {
    const m = countdown.overdueMinutes;
    const s = countdown.overdueSeconds;
    const label = m === 0 ? `${s}s overdue` : `${m}m ${s}s overdue`;
    return { label, isOverdue: true, minutes: m, seconds: s };
  }

  const m = countdown.remainingMinutes;
  const s = countdown.remainingSeconds;
  return {
    label: `${m}m ${s}s`,
    isOverdue: false,
    minutes: m,
    seconds: s,
  };
}

const inquiryDisplay = {
  masterRows,
  optionValue,
  optionalId,
  optionalBool,
  compactFilters,
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveInquiryBreadcrumb,
  resolveCurrentSlaWindow,
};

export default inquiryDisplay;
